import { bigintTransformers, booleanTransformers, createNumericTransformers, integerTransformers, numberTransformers, numericEqual, objectTransformers, shallowEqual, } from "@knyt/artisan";
import { areStyleSheetMixinsEqual, areStyleSheetsEqual, StyleSheet, } from "@knyt/tailor";
/**
 * A builder for defining properties on an `KnytElement`.
 *
 * @internal scope: workspace
 */
/*
 * ### Private Remarks
 *
 * This is only exported to avoid the following error when using `defineProperty`:
 *
 * > TS7056: The inferred type of this node exceeds the maximum length
 * > the compiler will serialize. An explicit type annotation is needed.
 *
 * When this isn't exported, the inferred type from `PropertyBuilder` is
 * too large for the compiler to serialize.
 */
export class PropertyBuilder {
    /**
     * A string that describes the type of the property,
     * which is used for debugging purposes.
     */
    #logType = "unknown";
    /** @public */
    attributeName;
    /** @public */
    toAttributeValue;
    /** @public */
    toPropertyValue;
    /** @public */
    comparator;
    /**
     * Sets the attribute name to which the property value will be reflected.
     *
     * @public
     */
    attribute(attributeName) {
        this.attributeName = attributeName;
        return this;
    }
    /**
     * Sets the attribute name to which the property value will be reflected.
     *
     * @remarks
     *
     * Shorthand for calling `attribute()` method.
     *
     * @see {@link attribute}
     */
    attr(attributeName) {
        return this.attribute(attributeName);
    }
    /**
     * Defines the property value as a string.
     *
     * @public
     */
    string() {
        this.#logType = "string";
        return this;
    }
    /**
     * Defines the property value as a string.
     *
     * @remarks
     *
     * Shorthand for calling `string()` method.
     *
     * @see {@link string}
     * @public
     */
    get str() {
        return this.string();
    }
    /**
     * Defines the property value as a boolean.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a boolean
     * property value to and from an attribute value.
     *
     * @public
     */
    boolean() {
        this.#logType = "boolean";
        Object.assign(this, booleanTransformers);
        return this;
    }
    /**
     * Defines the property value as a boolean.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a boolean
     * property value to and from an attribute value.
     *
     * Shorthand for calling `boolean()` method.
     *
     * @see {@link boolean}
     * @public
     */
    get bool() {
        return this.boolean();
    }
    /**
     * Defines the property value as a number.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a number
     * property value to and from an attribute value.
     *
     * @public
     */
    number() {
        this.#logType = "number";
        Object.assign(this, numberTransformers);
        return this;
    }
    /**
     * Defines the property value as a number.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a number
     * property value to and from an attribute value.
     *
     * @remarks
     *
     * Shorthand for calling `number()` method.
     *
     * @see {@link number}
     * @public
     */
    get num() {
        return this.number();
    }
    /**
     * Defines the property value as an integer.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a integer
     * property value to and from an attribute value.
     *
     * @public
     */
    integer() {
        this.#logType = "integer";
        Object.assign(this, integerTransformers);
        return this;
    }
    /**
     * Defines the property value as an integer.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a integer
     * property value to and from an attribute value.
     *
     * Shorthand for calling `integer()` method.
     *
     * @see {@link integer}
     * @public
     */
    get int() {
        return this.integer();
    }
    /**
     * Defines the property value as a bigint.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a bigint
     * property value to and from an attribute value.
     *
     * @public
     */
    bigInteger() {
        this.#logType = "bigint";
        Object.assign(this, bigintTransformers);
        return this;
    }
    /**
     * Defines the property value as a bigint.
     *
     * @remarks
     *
     * Sets up transformation functions for converting a bigint
     * property value to and from an attribute value.
     *
     * Shorthand for calling `bigInteger()` method.
     *
     * @see {@link bigint}
     * @public
     */
    get bigint() {
        return this.bigInteger();
    }
    /**
     * Defines the property value as a numeric object.
     *
     * @remarks
     *
     * Numeric values are those that can be converted to a number using
     * the `Number()` constructor. This includes objects with a `valueOf()`
     * method returning a number, or primitives convertible to numbers.
     * e.g. `Date`.
     *
     * Sets up transformation functions for converting a numeric property
     * value to and from an attribute value.
     *
     * @example
     *
     * ```ts
     * define.prop.numeric(Date);
     * ```
     *
     * @public
     */
    numeric(Constructor) {
        this.#logType = "numeric";
        Object.assign(this, createNumericTransformers(Constructor));
        this.comparator = numericEqual;
        return this;
    }
    /**
     * Defines the property value as a DOM element reference.
     *
     * @public
     */
    elementRef() {
        this.#logType = "elementRef";
        return this;
    }
    /**
     * Defines the property value as an observer.
     *
     * @public
     */
    /*
     * ### Private Remarks
     *
     * This shouldn't be names "subscriber", because that name implies
     * that a subscription is created, which is not the case.
     * An observer can be used to observe _anything_.
     */
    observer() {
        this.#logType = "observer";
        return this;
    }
    /**
     * Defines the property value as a shallow object or array.
     *
     * @remarks
     *
     * Because this property is a shallow object, the `shallowEqual`
     * function is used to compare the property value with the previous value.
     * As a result, it should be used as the last method called in the chain.
     *
     * @alpha This is an experimental feature and may change in the future.
     */
    shallow() {
        this.#logType = "shallow";
        Object.assign(this, objectTransformers);
        // Type cast to avoid unhelpful error message.
        this.comparator = shallowEqual;
        return this;
    }
    /**
     * Defines the property value as a style object.
     *
     * @remarks
     *
     * Because this property is a shallow object, the `shallowEqual`
     * function is used to compare the property value with the previous value.
     * As a result, it should be used as the last method called in the chain.
     */
    styleObject() {
        this.#logType = "styleObject";
        return this.shallow();
    }
    /**
     * Defines the property value as a style sheet.
     *
     * @remarks
     *
     * Because this property sets a comparator function,
     * it should be used as the last method called in the chain.
     */
    styleSheet() {
        this.#logType = "styleSheet";
        // Type cast to avoid unhelpful error message.
        this.comparator = areStyleSheetsEqual;
        return this;
    }
    /**
     * Defines the property value as a style sheet mixin.
     *
     * @remarks
     *
     * Because this property sets a comparator function,
     * it should be used as the last method called in the chain.
     *
     * @alpha This is an experimental feature and may change in the future.
     */
    styleSheetMixin() {
        this.#logType = "styleSheetMixin";
        Object.assign(this, objectTransformers);
        // Type cast to avoid unhelpful error message.
        this.comparator = areStyleSheetMixinsEqual;
        return this;
    }
    /**
     * Sets the property's comparator function.
     *
     * @remarks
     *
     * This is used to compare the property value with the previous value.
     * It should be used as the last method called in the chain.
     */
    equality(comparator) {
        this.comparator = comparator;
        return this;
    }
    get [Symbol.toStringTag]() {
        return `[PropertyBuilder: ${this.#logType}${this.attributeName ? `, ${this.attributeName}` : ""}]`;
    }
    [Symbol.for("nodejs.util.inspect.custom")]() {
        return this[Symbol.toStringTag];
    }
}
/**
 * Defines a reactive property, optionally with an attribute name to reflect
 * the property value.
 *
 * @remarks
 *
 *
 * Commonly used for properties and attributes on `KnytElement` instances or
 * Knyt Glazier includes.
 *
 * @public
 */
// TODO: Consider adding an overload to support providing a type constructor.
export function defineProperty() {
    const propertyBuilder = new PropertyBuilder();
    // Disable attribute integration by default.
    propertyBuilder.attributeName = false;
    return propertyBuilder;
}
